/* eslint-disable no-console */

/**
 * Image Lightbox.
 */
export default class ImageLightbox {
  /**
   * Initial expanded state.
   * @private
   *
   * @type {Boolean}
   */
  #expanded = false; // eslint-disable-line react/sort-comp

  /**
   * Start the component
   */
  constructor(controller) {
    this.controller = controller;
    this.target = this.getLightboxElement();

    if (this.target !== null && this.controller !== null) {
      this.setup();
    }
  }

  /**
   * Set expanded state.
   *
   * @param {boolean} newState The new expanded state.
   */
  set expanded(newState) {
    this.#expanded = newState;

    this.controller.setAttribute('aria-expanded', this.expanded);
    this.target.setAttribute('aria-hidden', !this.expanded);
    this.target.setAttribute('tabindex', this.expanded ? '0' : '-1');

    if (this.expanded) {
      // Only do this once.
      if (!this.imageLoaded) {
        this.image.src = this.imgSrc;
      }

      this.target.focus();
    }
  }

  /**
   * Get expanded state.
   *
   * @return {boolean}
   */
  get expanded() {
    return this.#expanded;
  }

  /**
   * Set a new lightbox target.
   *
   * @param {HTMLElement} target The new target for this controller.
   */
  set newTarget(target) {
    if (target !== null) {
      this.removeTarget();

      this.target = target;
      const img = target.querySelector('img');

      if (this.controller.nodeName === 'A' && img !== null) {
        this.controller.setAttribute('href', img.dataset?.src);
      }
      this.setup();
    }
  }

  /**
   * Get the target element based on the controller's `aria-controls` attribute.
   *
   * @param  {HTMLButtonElement} controller The component's controlling element.
   * @return {HTMLElement|null}
   */
  getLightboxElement = () => {
    let target = null;

    // Use the aria-controls attribute value to find the target element.
    if (this.controller.hasAttribute('aria-controls')) {
      const elementControls = this.controller.getAttribute('aria-controls');
      target = document.getElementById(elementControls);

      if (target === null) {
        // eslint-disable-next-line max-len
        console.error(`Configuration error: A target element with \`id="${elementControls}"\` is not found`);
      }
    }

    return target;
  }

  /**
   * Set up the component's DOM attributes and event listeners.
   */
  setup = () => {
    const closeButton = this.target.querySelector('.image-lightbox__close');
    const image = this.target.querySelector('img');
    const container = this.target.querySelector('.image-lightbox__container');

    /**
     * The lightbox content wrapper, excluding the backdrop.
     *
     * @type {HTMLElement}
     */
    this.container = container;

    /**
     * The lightbox close button.
     *
     * @type {HTMLButtonElement}
     */
    this.closeButton = closeButton;

    /**
     * The lightbox image.
     *
     * @type {HTMLImageElement}
     */
    this.image = image;

    /**
     * The image `src` value, held in the data-src attribute until the lightbox
     * is activated.
     *
     * @type {string}
     */
    this.imgSrc = this.image.dataset?.src;

    /**
     * Initial image loaded state.
     *
     * @type {Boolean}
     */
    this.imageLoaded = false;

    if (this.imgSrc && this.closeButton) {
      // Treat links as buttons.
      if (this.controller.nodeName !== 'BUTTON') {
        this.controller.setAttribute('role', 'button');
        this.controller.addEventListener('keydown', this.handleControllerKeydown);
      }

      this.controller.setAttribute('aria-expanded', this.expanded);
      this.target.setAttribute('tabindex', '-1');

      // Add event listeners
      this.image.addEventListener('load', this.handleImageLoaded);
      this.controller.addEventListener('click', this.handleControllerClick);
      this.target.addEventListener('keydown', this.handleTargetKeydown);
      this.closeButton.addEventListener('click', this.handleCloseButtonClick);
      this.target.addEventListener('click', this.handleBackgroundClick);
    }
  }

  /**
   * Remove loading state when the image is loaded.
   */
  handleImageLoaded = () => {
    this.imageLoaded = true;

    this.target.classList.remove('image-lightbox--loading');
  }

  /**
   * Handle lightbox controller clicks.
   *
   * @param {Event} event The event object.
   */
  handleControllerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.expanded = true;
  }

  /**
   * Handle controller keydown.
   *
   * @param {Event} event The event object.
   */
  handleControllerKeydown = (event) => {
    if (event.key === ' ') {
      event.preventDefault();

      this.expanded = true;
    }
  }

  /**
   * Handle keydown events on the lightbox.
   *
   * @param {Event} event The event object.
   */
  handleTargetKeydown = (event) => {
    const { code, shiftKey } = event;
    const { activeElement } = document;

    if (this.expanded) {
      switch (code) {
        // Hide the lightbox when the Escape key is pressed.
        case 'Escape':
          event.preventDefault();

          this.expanded = false;
          this.controller.focus();

          break;

        // Trap tabs within the modal.
        case 'Tab':
          if (shiftKey) {
            if (this.target === activeElement) {
              event.preventDefault();

              this.closeButton.focus();
            }
          } else if (this.closeButton === activeElement) {
            event.preventDefault();

            this.target.focus();
          }

          break;

        default:
          break;
      }
    }
  }

  /**
   * Handle click events on the close button.
   *
   * @param {Event} event The event object.
   */
  handleCloseButtonClick = (event) => {
    if (this.expanded) {
      event.preventDefault();

      this.expanded = false;
      this.controller.focus();
    }
  }

  /**
   * Close the lightbox when clicking anywhere outside of the lightbox content.
   *
   * @param {Event} event The event object.
   */
  handleBackgroundClick = (event) => {
    const { target: clicked } = event;

    if (this.expanded && !this.container.contains(clicked)) {
      event.preventDefault();
      this.expanded = false;
    }
  }

  /**
   * Reset the active target in preparation for assigning a new one.
   */
  removeTarget = () => {
    this.target.setAttribute('aria-hidden', 'true');
    this.target.removeAttribute('tabindex');

    this.target.removeEventListener('keydown', this.handleTargetKeydown);
    this.target.removeEventListener('click', this.handleBackgroundClick);
    this.image.removeEventListener('load', this.handleImageLoaded);
    this.closeButton.removeEventListener('click', this.handleCloseButtonClick);

    this.image = null;
    this.closeButton = null;
  };

  /**
   * Remove attributes and event listeners.
   */
  destroy = () => {
    this.controller.removeAttribute('role');
    this.controller.removeAttribute('aria-expanded');

    this.controller.removeEventListener('click', this.handleControllerClick);

    this.removeTarget();

    // Set the property directly to avoid running the setter.
    this.#expanded = false;
  }
}
